import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, Monitor, RefreshCw } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import CamembertMarque from "@/components/CamembertMarque";

const ETATS = {
  rouge: { label: "Panne critique", point: "bg-red-500", cadre: "border-red-200 bg-red-50", texte: "text-red-700" },
  orange: { label: "À surveiller", point: "bg-amber-500", cadre: "border-amber-200 bg-amber-50", texte: "text-amber-700" },
  vert: { label: "Tout va bien", point: "bg-emerald-500", cadre: "border-emerald-200 bg-emerald-50", texte: "text-emerald-700" },
  silencieux: { label: "Aucune alerte reçue", point: "bg-slate-300", cadre: "border-slate-200 bg-white", texte: "text-slate-500" },
};

const Compteur = ({ label, valeur, point, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid={testid}>
    <div className="flex items-center gap-2 bk-label mb-1">
      {point && <span className={`h-2 w-2 rounded-full ${point}`} />} {label}
    </div>
    <div className="font-display text-2xl font-black tracking-tighter text-slate-900">{valeur}</div>
  </div>
);

export default function AdminSupervision() {
  const [data, setData] = useState(null);
  const [maj, setMaj] = useState(null);

  const load = useCallback(() => {
    api
      .get("/admin/supervision")
      .then(({ data }) => {
        setData(data);
        setMaj(new Date());
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, [load]);

  const sites = data?.sites || [];
  const r = data?.resume;

  return (
    <div className="space-y-6" data-testid="admin-supervision">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="bk-label mb-2 flex items-center gap-2">
            <Activity size={13} /> Temps réel · actualisé toutes les 30 s
          </div>
          <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900">Supervision des sites</h1>
          <p className="text-sm text-slate-600 mt-2">
            État de chaque site équipé d'une clé d'alerte, d'après les alarmes reçues des dernières 24 h et les tickets
            de supervision encore ouverts.
          </p>
        </div>
        <button
          data-testid="supervision-refresh"
          onClick={load}
          className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500 hover:text-[#005CFF] transition-colors"
        >
          <RefreshCw size={14} /> {maj ? `Mis à jour à ${maj.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}` : "Actualiser"}
        </button>
      </div>

      <Link
        to="/admin/mural"
        data-testid="supervision-mural-link"
        className="inline-flex items-center gap-2 text-sm font-semibold text-[#005CFF] hover:underline"
      >
        <Monitor size={15} /> Ouvrir l'écran mural de l'atelier
      </Link>

      {r && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Compteur label="Sites supervisés" valeur={r.sites} testid="sup-kpi-sites" />
          <Compteur label="Panne critique" valeur={r.rouge} point={ETATS.rouge.point} testid="sup-kpi-rouge" />
          <Compteur label="À surveiller" valeur={r.orange} point={ETATS.orange.point} testid="sup-kpi-orange" />
          <Compteur label="Tout va bien" valeur={r.vert} point={ETATS.vert.point} testid="sup-kpi-vert" />
          <Compteur label="Alertes 24 h" valeur={r.alertes_24h} testid="sup-kpi-alertes" />
        </div>
      )}

      {data?.charge_du_jour && (
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="charge-du-jour">
          <div className="bk-label mb-3">Charge du jour</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <div className="font-display text-2xl font-black tracking-tighter text-slate-900">{data.charge_du_jour.a_traiter_aujourdhui}</div>
              <div className="text-xs text-slate-500">à traiter aujourd'hui</div>
            </div>
            <div>
              <div className="font-display text-2xl font-black tracking-tighter text-red-600">{data.charge_du_jour.bloquants}</div>
              <div className="text-xs text-slate-500">bloquants</div>
            </div>
            <div>
              <div className="font-display text-2xl font-black tracking-tighter text-slate-900">{data.charge_du_jour.tickets_ouverts}</div>
              <div className="text-xs text-slate-500">tickets ouverts</div>
            </div>
            <div>
              <div className="font-display text-2xl font-black tracking-tighter text-slate-900">{data.charge_du_jour.heures_restantes} h</div>
              <div className="text-xs text-slate-500">restantes aujourd'hui</div>
            </div>
          </div>
          {data.charge_du_jour.journee_pleine && (
            <p className="text-xs font-semibold text-amber-700 mt-3" data-testid="journee-pleine">
              Journée pleine : il reste des tickets dus aujourd'hui et moins d'une heure ouvrée devant nous.
            </p>
          )}
        </div>
      )}

      {data?.marques?.length > 0 && (
        <div>
          <div className="bk-label mb-3">État du parc supervisé par marque</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="camemberts-marques">
            {data.marques.map((m) => (
              <CamembertMarque key={m.cle} marque={m} />
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Les camemberts se remplissent au fur et à mesure que vous enregistrez le matériel à surveiller dans la fiche
            client (Clients → matériel).
          </p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {sites.map((s) => {
          const e = ETATS[s.etat] || ETATS.silencieux;
          return (
            <div key={s.id} data-testid={`supervision-site-${s.id}`} className={`rounded-xl border p-5 ${e.cadre}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{s.libelle}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {s.total_alertes} alerte(s) au total
                    {s.derniere_alerte ? ` · dernière ${fmtDate(s.derniere_alerte)}` : " · aucune"}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`h-2.5 w-2.5 rounded-full ${e.point} ${s.etat === "rouge" ? "animate-pulse" : ""}`} />
                  <span className={`text-xs font-semibold ${e.texte}`} data-testid={`supervision-etat-${s.id}`}>{e.label}</span>
                </div>
              </div>

              {s.alertes_24h > 0 && (
                <div className="text-xs text-slate-600 mt-3">
                  {s.alertes_24h} alerte(s) sur 24 h · dernière : {s.derniere_source} — {s.dernier_message || "sans message"}
                </div>
              )}

              {s.tickets_ouverts.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  <div className="bk-label">Tickets de supervision ouverts</div>
                  {s.tickets_ouverts.map((t) => (
                    <Link
                      key={t.reference}
                      to="/admin/tickets"
                      data-testid={`supervision-ticket-${t.reference}`}
                      className="block text-xs text-slate-700 hover:text-[#005CFF]"
                    >
                      <span className="font-mono-bk">{t.reference}</span> · {t.domaine}
                      {t.materiel ? ` · ${t.materiel}` : ""} · {t.urgence}
                      {t.prise_en_charge ? ` · prise en charge ${t.prise_en_charge}` : ""}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {sites.length === 0 && (
        <p className="text-sm text-slate-500" data-testid="supervision-empty">
          Aucun site supervisé. Créez une clé d'alerte dans « Alertes équipements » pour voir apparaître un site ici.
        </p>
      )}
    </div>
  );
}
